import { type FC, useCallback, useEffect, useState } from "react"

import type { LandingPageTwoColumnsBlock } from "@forento/core/models/landing"
import { landingPageCopyCharacterLimits } from "@forento/core/schemas/landing"
import { hasLandingPageLinkTarget, type LandingPageLink } from "@forento/core/utilities/landingLink"
import {
	getVimeoIdFromUrl,
	getVimeoUrlFromId,
	getYouTubeIdFromUrl,
	getYouTubeUrlFromId,
} from "@forento/core/utilities/url"
import { VimeoPlayer, YoutubePlayer } from "@forento/ui/components/VideoPlayer"
import { cn } from "@forento/ui/utilities/tailwind"

import Dropdown from "~/components/Dropdown"
import ImageUploader from "~/components/ImageUploader"
import InputField from "~/components/InputField"
import useFileUpload from "~/hooks/useFileUpload"

import ButtonLinkCard from "./ButtonLinkCard"
import EditableField from "./EditableField"
import EditorShell from "./EditorShell"
import RichEditableField from "./RichEditableField"

type Props = {
	block: LandingPageTwoColumnsBlock
	onChange: (block: LandingPageTwoColumnsBlock) => void
	onClose: () => void
}
const TwoColumnsBlockEditor: FC<Props> = ({ block, onChange, onClose }) => {
	const [imageUrl, setImageUrl] = useState(block.content.image?.url ?? "")
	const [imageAlt, setImageAlt] = useState(block.content.image?.alt ?? "")
	const [mediaType, setMediaType] = useState<"image" | "youtube" | "vimeo" | "">(
		block.content.media?.type ?? (block.content.image ? "image" : ""),
	)
	const [videoUrl, setVideoUrl] = useState(
		block.content.media?.type === "youtube"
			? getYouTubeUrlFromId(block.content.media.videoId)
			: block.content.media?.type === "vimeo"
				? getVimeoUrlFromId(block.content.media.videoId)
				: "",
	)
	const [title, setTitle] = useState(block.content.title)
	const [description, setDescription] = useState(block.content.description)
	const [buttonLabel, setButtonLabel] = useState(block.content.button?.label ?? "")
	const [buttonLink, setButtonLink] = useState<LandingPageLink>(
		block.content.button?.link ?? { type: "page", path: "" },
	)
	const [inverted, setInverted] = useState(block.content.inverted)

	const handleDescriptionChange = useCallback((value: string | null) => {
		setDescription(value ?? "")
	}, [])

	const imageUpload = useFileUpload()

	useEffect(() => {
		const media =
			mediaType === "youtube"
				? getYouTubeIdFromUrl(videoUrl.trim())
				: mediaType === "vimeo"
					? getVimeoIdFromUrl(videoUrl.trim())
					: null

		onChange({
			type: "two-columns",
			content: {
				image:
					mediaType === "image" && imageUrl.trim()
						? { url: imageUrl.trim(), alt: imageAlt.trim() }
						: undefined,
				media:
					mediaType === "youtube" && media
						? { type: "youtube", videoId: media }
						: mediaType === "vimeo" && media
							? { type: "vimeo", videoId: media }
							: undefined,
				title: title.trim(),
				description: description.trim(),
				button:
					buttonLabel.trim() && hasLandingPageLinkTarget(buttonLink)
						? { label: buttonLabel.trim(), link: buttonLink }
						: null,
				inverted,
			},
		})
	}, [buttonLabel, buttonLink, description, imageAlt, imageUrl, inverted, mediaType, onChange, title, videoUrl])

	const hasButton = buttonLabel.trim().length > 0 || hasLandingPageLinkTarget(buttonLink)
	const handleImageUpload = async (file: File) => {
		const uploaded = await imageUpload.upload({ file, kind: "landing-image" })
		if (uploaded === null) return
		setImageUrl(uploaded.publicUrl)
	}
	const media = (() => {
		if (mediaType === "image" && imageUrl) return { type: "image" as const, url: imageUrl, alt: imageAlt }
		if (mediaType === "youtube") {
			const videoId = getYouTubeIdFromUrl(videoUrl.trim())
			return videoId ? { type: "youtube" as const, videoId } : null
		}
		if (mediaType === "vimeo") {
			const videoId = getVimeoIdFromUrl(videoUrl.trim())
			return videoId ? { type: "vimeo" as const, videoId } : null
		}
		return null
	})()

	return (
		<EditorShell onClose={onClose}>
			<section className="px-6 pt-16 pb-8 sm:pt-20 lg:px-8">
				<div
					className={cn(
						"mx-auto grid max-w-7xl gap-8 lg:grid-cols-2 lg:items-center lg:gap-12",
						inverted && "lg:grid-flow-dense",
					)}
				>
					<div className={cn("relative", inverted && "lg:col-start-2")}>
						{media?.type === "image" ? (
							<img
								src={media.url}
								alt={media.alt}
								className="h-auto max-h-96 w-full max-w-full object-contain"
							/>
						) : media?.type === "youtube" ? (
							<YoutubePlayer
								videoId={media.videoId}
								className="aspect-video w-full rounded-lg bg-gray-50"
							/>
						) : media?.type === "vimeo" ? (
							<VimeoPlayer
								videoId={media.videoId}
								className="aspect-video w-full rounded-lg bg-gray-50"
							/>
						) : (
							<div className="flex aspect-video w-full items-center justify-center rounded-lg bg-gray-100 text-gray-400">
								Add media
							</div>
						)}
					</div>
					<div className={cn("flex flex-col justify-center", inverted && "lg:col-start-1 lg:row-start-1")}>
						<EditableField
							value={title}
							onChange={setTitle}
							placeholder="Title"
							maxCharacters={landingPageCopyCharacterLimits.title}
							inputClassName="font-bold text-3xl text-gray-900 tracking-tight sm:text-4xl"
						/>
						<RichEditableField
							initialValue={block.content.description}
							onChange={handleDescriptionChange}
							className="mt-4"
							maxCharacters={landingPageCopyCharacterLimits.body}
						/>
						{hasButton ? (
							<div className="mt-8 flex flex-wrap items-center gap-4">
								<div className="landing-button inline-flex max-w-full items-center justify-center px-6 py-3">
									<EditableField
										value={buttonLabel}
										onChange={setButtonLabel}
										placeholder="Button label"
										fitContent
										maxCharacters={landingPageCopyCharacterLimits.shortLabel}
										showCharacterCounter={false}
										inputClassName="min-w-32 bg-transparent text-center font-semibold text-base"
									/>
								</div>
								<button
									type="button"
									onClick={() => {
										setButtonLabel("")
										setButtonLink({ type: "page", path: "" })
									}}
									className="cursor-pointer text-sm font-medium text-gray-500 transition-colors hover:text-gray-800"
								>
									Remove button
								</button>
							</div>
						) : (
							<button
								type="button"
								onClick={() => setButtonLabel("Learn more")}
								className="mt-8 inline-flex w-fit cursor-pointer items-center rounded-full border border-dashed border-gray-300 px-5 py-3 text-sm font-medium text-gray-500 transition-colors hover:border-gray-400 hover:text-gray-800"
							>
								Add button
							</button>
						)}
					</div>
				</div>
			</section>
			<section className="px-6 pb-8 lg:px-8">
				<div className="mx-auto grid max-w-7xl gap-4 lg:grid-cols-3">
					<div className="rounded-2xl border border-black/10 bg-white p-5 shadow-sm">
						<div className="mb-3 text-sm font-semibold text-gray-900">Media</div>
						<Dropdown
							items={[
								{ id: "", title: "None" },
								{ id: "image", title: "Image" },
								{ id: "youtube", title: "YouTube" },
								{ id: "vimeo", title: "Vimeo" },
							]}
							selectedItemId={mediaType}
							onChange={id => {
								setMediaType(id as "image" | "youtube" | "vimeo" | "")
								setVideoUrl("")
							}}
							className="mb-4"
						/>
						{mediaType === "" && <p className="text-sm text-gray-500">Choose image or embedded video.</p>}
						{mediaType === "image" && (
							<>
								<ImageUploader
									filePath={imageUrl || null}
									onUpload={handleImageUpload}
									onDelete={() => setImageUrl("")}
								/>
								<InputField
									label="Image alt"
									value={imageAlt}
									onChange={setImageAlt}
									placeholder="Alt text"
									inline
								/>
							</>
						)}
						{(mediaType === "youtube" || mediaType === "vimeo") && (
							<InputField
								label={`${mediaType === "youtube" ? "YouTube" : "Vimeo"} URL`}
								value={videoUrl}
								onChange={setVideoUrl}
								placeholder="https://..."
								inline
							/>
						)}
					</div>
					{hasButton && <ButtonLinkCard value={buttonLink} onChange={setButtonLink} />}
					<div className="rounded-2xl border border-black/10 bg-white p-5 shadow-sm">
						<div className="mb-3 text-sm font-semibold text-gray-900">Layout</div>
						<label className="flex cursor-pointer items-center gap-2.5">
							<input
								type="checkbox"
								checked={inverted}
								onChange={e => setInverted(e.target.checked)}
								className="cursor-pointer rounded border-stone-300 text-stone-700"
							/>
							<span className="text-sm text-stone-600">Place the media on the right</span>
						</label>
					</div>
				</div>
			</section>
		</EditorShell>
	)
}

export default TwoColumnsBlockEditor
